import React from 'react';
import { AvTooltip, FieldListObject, SelectedFieldsType } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Button, Collapse, List, Typography, useTheme } from '@mui/material';
import useStyle from '../../../components/FieldsToolbar/useStyle';
import { FieldType } from '../types/types';
import FieldItem from './FieldItem';

interface EntityProps {
  displayName: string;
  fieldList: FieldListObject[];
  expanded: boolean;
  handleExpand: any;
  fieldType: FieldType;
  matchHighlightResults: string[];
  onFieldClick;
  selectedFields: SelectedFieldsType;
  page: string;
  measurementsObject?: React.ComponentProps<typeof FieldItem>['measurementsObject'];
  calcDimsObject?: React.ComponentProps<typeof FieldItem>['calcDimsObject'];
  shouldDisableField?: (field: FieldListObject) => { disabled: boolean; disabledTooltip: string };
  enabledFieldTypesWithOptions?: Set<string | undefined>;
  onApplyExpression?: ({ field, type, selectedValue }: { field: FieldListObject; type: string; selectedValue: any }) => void;
}
const EntityItem: React.FC<EntityProps> = ({
  displayName,
  fieldList: items,
  expanded,
  handleExpand,
  fieldType,
  matchHighlightResults,
  onFieldClick,
  selectedFields,
  page,
  measurementsObject,
  calcDimsObject,
  shouldDisableField,
  enabledFieldTypesWithOptions,
  onApplyExpression,
}) => {
  const theme = useTheme();
  const style = useStyle(page);

  return (
    <Box key={displayName}>
      <Button
        sx={{
          ...flex.justifyStart,
          ':hover': {
            backgroundColor: style.entityBackgroundColor,
          },
          py: 1,
          position: 'sticky',
          top: -2,
          backgroundColor: style.entityBackgroundColor,
          zIndex: 1,
          width: '100%',
        }}
        onClick={() => handleExpand(displayName)}
        disabled={!items.length}>
        <ArrowDown
          style={{
            ...iconSize(16),
            opacity: !items.length ? 0.4 : 1,
            color: 'var(--color-content-base-tertiary)',
            transform: `rotate(${expanded ? 0 : -90}deg)`,
            transition: theme.transitions.create(['transform'], { duration: theme.transitions.duration.shorter }),
          }}
        />
        <Typography variant="h7" sx={{ ...flex.row, opacity: !items.length ? 0.4 : 1, px: '2px', fontSize: 11 }}>
          <AvTooltip>
            {displayName} ({items.length})
          </AvTooltip>
        </Typography>
      </Button>
      <Box sx={{ ...flex.col, gap: 2 }}>
        <Collapse in={!!(expanded && items?.length)} unmountOnExit timeout={200}>
          <List sx={{ ...flex.col, p: 0 }}>
            {items.map(field => (
              <FieldItem
                key={field.value}
                field={field}
                fieldType={fieldType}
                matchHighlightResults={matchHighlightResults}
                page={page}
                selectedFields={selectedFields}
                onFieldClick={onFieldClick}
                measurementsObject={measurementsObject}
                calcDimsObject={calcDimsObject}
                enabledFieldTypesWithOptions={enabledFieldTypesWithOptions}
                {...(shouldDisableField && shouldDisableField(field))}
                onApplyExpression={onApplyExpression}
              />
            ))}
          </List>
        </Collapse>
      </Box>
    </Box>
  );
};

export default EntityItem;
